import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { removeItem, clearAllSession, getItemJSON } from '../utils/storage';
import './Header.css';

function Header({ user, setUser }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [notifications, setNotifications] = useState([]);

  // Language display names in their native scripts
  const languageNames = {
    'en': 'English',
    'hi': 'हिन्दी',
    'te': 'తెలుగు'
  };

  const handleLogout = () => {
    // Clear all session data (expires when browser closes anyway)
    clearAllSession();
    
    // Also clear localStorage items that might have been set
    localStorage.removeItem('candidateLoggedIn');
    localStorage.removeItem('employerLoggedIn');
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('candidateData');
    
    // Update the user state
    setUser(null);
    
    // Navigate to home page
    navigate('/');
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  // Close dropdown when clicking outside or pressing Escape
  const langRef = useRef(null);

  // Fetch all active notifications on component mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch('https://tstranscareers.in/api/notification/active');
        const data = await response.json();
        if (data.success && data.notifications && Array.isArray(data.notifications)) {
          setNotifications(data.notifications);
        } else if (data.success && data.notification) {
          // Fallback for single notification response
          setNotifications([data.notification]);
        } else {
          setNotifications([]);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotifications([]);
      }
    };

    fetchNotifications();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLanguageDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language); // This will be 'English', 'हिन्दी', or 'తెలుగు'
    setIsLanguageDropdownOpen(false);
    const map = { 'English': 'en', 'हिन्दी': 'hi', 'తెలుగు': 'te' };
    const lng = map[language] || 'en';
    
    // Use Google Translate to translate entire page
    translatePageWithGoogle(lng);
    
    // Also update i18n for any custom translations
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    localStorage.setItem('selectedLanguageName', language); // Store display name
  };

  const translatePageWithGoogle = (languageCode) => {
    // If switching to English, clear all translation cookies and reload
    if (languageCode === 'en') {
      // Clear Google Translate cookies
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = 'googtrans=/en/en; path=/;';
      document.cookie = `googtrans=/en/en; path=/; domain=${window.location.hostname};`;
      
      // Remove Google Translate elements
      const gtElements = document.querySelectorAll('.skiptranslate, .goog-te-banner-frame, font[style*="vertical-align: inherit"]');
      gtElements.forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      
      // Reload page to show original English
      setTimeout(() => {
        window.location.reload();
      }, 100);
      return;
    }

    // For non-English languages (Hindi, Telugu)
    // Remove existing Google Translate element
    const existingGoogleTranslate = document.getElementById('google_translate_element');
    if (existingGoogleTranslate) {
      existingGoogleTranslate.innerHTML = '';
    }

    // Load Google Translate script if not already loaded
    if (!window.googleTranslateElementInit) {
      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);

      // Initialize Google Translate
      window.googleTranslateElementInit = function() {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,hi,te',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false
          },
          'google_translate_element'
        );
      };
    }

    // Set translation cookies
    document.cookie = `googtrans=/en/${languageCode}; path=/;`;
    document.cookie = `googtrans=/en/${languageCode}; path=/; domain=${window.location.hostname};`;
    
    // Reload page to apply translation
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Display languages in their native scripts
  const languages = [
    'English',
    'తెలుగు',  // Telugu
    'हिन्दी',   // Hindi
  ];

  // Initialize Google Translate on component mount
  useEffect(() => {
    // Restore selected language name from localStorage
    const savedLanguageName = localStorage.getItem('selectedLanguageName');
    const savedLanguageCode = localStorage.getItem('i18nextLng') || 'en';
    
    if (savedLanguageName) {
      setSelectedLanguage(savedLanguageName);
    } else {
      // Set default based on language code
      setSelectedLanguage(languageNames[savedLanguageCode] || 'English');
    }

    // Create hidden Google Translate element
    if (!document.getElementById('google_translate_element')) {
      const div = document.createElement('div');
      div.id = 'google_translate_element';
      div.style.display = 'none';
      document.body.appendChild(div);
    }

    // Check if language was previously set
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    
    if (savedLanguage === 'en') {
      // Clear any existing translation cookies for English
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = 'googtrans=/en/en; path=/;';
      document.cookie = `googtrans=/en/en; path=/; domain=${window.location.hostname};`;
    } else {
      // Apply Google Translate for saved language (Hindi or Telugu)
      document.cookie = `googtrans=/en/${savedLanguage}; path=/;`;
      document.cookie = `googtrans=/en/${savedLanguage}; path=/; domain=${window.location.hostname};`;
    }

    // Load Google Translate script
    if (!window.googleTranslateElementInit) {
      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);

      window.googleTranslateElementInit = function() {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,hi,te',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false
          },
          'google_translate_element'
        );
      };
    }

    return () => {
      // Cleanup
      const googleTranslateElement = document.getElementById('google_translate_element');
      if (googleTranslateElement && googleTranslateElement.parentNode === document.body) {
        document.body.removeChild(googleTranslateElement);
      }
    };
  }, []);

  return (
    <header className="header">
      {/* Helpline ticker with admin notifications integrated */}
      <div className="helpline-ticker" aria-hidden="false" role="region" aria-label="Helpline and Notifications">
        <div className="helpline-inner">
          {/* Admin Notifications - Shows all active notifications */}
          {notifications.length > 0 && notifications.map((notification, index) => (
            <div key={notification.id || index} className="helpline-item notification-item">
              {notification.message}
            </div>
          ))}
        </div>
      </div>
      {/* <div className="topbar">
        <div className="container topbar-inner">
          <div className="topbar-left">
            <span className="site-tagline">Government Job Services Portal</span>
          </div>
          <div className="topbar-right">
            <a href="#accessibility" className="topbar-link">Accessibility</a>
            <a href="#contact" className="topbar-link">Contact</a>
            <a href="#help" className="topbar-link">Help</a>
          </div>
        </div>
      </div> */}

      <div className="main-header">
        <div className="container main-header-inner">
          <div className="branding">
            <Link to="/" className="brand-link">
              <div className="logo-container">
                <img src="/assets/images/tglogo.png" alt="Portal Logo" className="brand-logo" />
                <img src="/assets/images/image1.jpeg" alt="Partner Logo" className="secondary-logo" />
                <div className="brand-text">
                  <span className="brand-title">{t('header.title')}</span>
                  <span className="brand-subtitle">{t('header.subtitle')}</span>
                </div>
              </div>
            </Link>
          </div>
          <div className="quick-actions">
            {/* Login/Logout buttons commented - will use later */}
            {/* {!user ? (
              <div className="auth-actions">
                <Link to="/login" className="btn small">{t('header.loginRegister')}</Link>
              </div>
            ) : (
              <div className="auth-actions">
                <button onClick={handleLogout} className="btn small danger">{t('header.logout')}</button>
              </div>
            )} */}
            <div className="language-selector google-translate-style notranslate" ref={langRef} translate="no">
              <button 
                className="language-button notranslate"
                onClick={toggleLanguageDropdown}
                aria-expanded={isLanguageDropdownOpen}
                aria-haspopup="listbox"
                title="Change Language"
                translate="no"
              >
                <svg className="translate-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="currentColor"/>
                </svg>
                <span className="current-lang notranslate" translate="no">{selectedLanguage}</span>
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                </svg>
              </button>
              {isLanguageDropdownOpen && (
                <div className="language-dropdown notranslate" role="listbox" translate="no">
                  {languages.map((language) => (
                    <div
                      key={language}
                      className={`language-option notranslate ${selectedLanguage === language ? 'selected' : ''}`}
                      role="option"
                      tabIndex={0}
                      onClick={() => handleLanguageSelect(language)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLanguageSelect(language); }}
                      translate="no"
                    >
                      <span className="lang-text notranslate" translate="no">{language}</span>
                      {selectedLanguage === language && (
                        <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                        </svg>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <div className="navbar">
        <div className="container navbar-inner">
          <nav className="nav">
            <Link to="/" className="nav-link">{t('header.home')}</Link>
            <Link to="/candidate-registration" className="nav-link">{t('header.candidateRegistration')}</Link>
            <Link to="/employer-registration" className="nav-link">{t('header.employerRegistration')}</Link>
           </nav>
        </div>
      </div> */}
    </header>
  );
}

export default Header;